
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { shouldUsePinMode } from '@/utils/pinModeUtils';

interface WeeklyReportData {
  weekStart: string;
  weekEnd: string;
  stats: {
    activeEmployees: number;
    totalHours: number;
    productivity: number;
    avgHoursPerDay: number;
  };
  dailyHours: Array<{ day: string; hours: number }>;
  employeePerformance: Array<{ name: string; hours: number }>;
  projectDistribution: Array<{ name: string; hours: number; percentage: number }>;
}

interface TimeEntry {
  id?: string;
  user_id: string;
  project_id: string;
  start_time: string;
  duration_minutes: number | null;
  hourly_rate?: number | null;
  projects?: { name: string } | null;
}

export const useWeeklyReportData = (customStart?: Date, customEnd?: Date) => {
  const [reportData, setReportData] = useState<WeeklyReportData>({
    weekStart: '',
    weekEnd: '',
    stats: {
      activeEmployees: 0,
      totalHours: 0,
      productivity: 0,
      avgHoursPerDay: 0,
    },
    dailyHours: [],
    employeePerformance: [],
    projectDistribution: [],
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchWeeklyReportData = useCallback(async () => {
    try {
      setLoading(true);

      let startOfWeek: Date;
      let endOfWeek: Date;

      if (customStart && customEnd) {
        startOfWeek = new Date(customStart);
        startOfWeek.setHours(0, 0, 0, 0);
        endOfWeek = new Date(customEnd);
        endOfWeek.setHours(23, 59, 59, 999);
      } else {
        const now = new Date();
        startOfWeek = new Date(now);
        const day = startOfWeek.getDay();
        startOfWeek.setDate(startOfWeek.getDate() - day + (day === 0 ? -6 : 1));
        startOfWeek.setHours(0, 0, 0, 0);
        endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
      }

      const usePinMode = shouldUsePinMode();
      let timeEntries: TimeEntry[] = [];
      let activeEmployeesCount = 0;
      let profilesMap = new Map<string, { first_name: string; last_name: string }>();
      let projectsMap = new Map<string, string>();

      if (usePinMode) {
        const pin = localStorage.getItem('manager_pin') || localStorage.getItem('employee_pin') || '';

        // Charger profils, projets et entrées en parallèle
        const [profilesRes, projectsRes, entriesRes] = await Promise.all([
          supabase.rpc('pin_list_profiles', { p_pin: pin }),
          supabase.rpc('pin_list_projects', { p_pin: pin }),
          supabase.rpc('pin_list_time_entries_range', {
            p_pin: pin,
            p_start: startOfWeek.toISOString(),
            p_end: endOfWeek.toISOString(),
            p_limit: 5000,
            p_offset: 0,
            p_order: 'asc',
          }),
        ]);

        if (profilesRes.error) throw profilesRes.error;
        if (projectsRes.error) throw projectsRes.error;
        if (entriesRes.error) throw entriesRes.error;

        const profiles = profilesRes.data || [];
        profiles.forEach((p: any) => profilesMap.set(p.id, { first_name: p.first_name, last_name: p.last_name }));

        const projects = projectsRes.data || [];
        projects.forEach((p: any) => projectsMap.set(p.id, p.name));

        timeEntries = (entriesRes.data || []).map((e: any) => ({
          ...e,
          projects: e.project_id ? { name: projectsMap.get(e.project_id) || 'Sans projet' } : null,
        }));
      } else {
        // Mode JWT
        const [entriesRes, profilesRes] = await Promise.all([
          supabase.from('time_entries').select('duration_minutes, start_time, user_id, projects(name)')
            .gte('start_time', startOfWeek.toISOString())
            .lte('start_time', endOfWeek.toISOString()),
          supabase.from('profiles').select('id, first_name, last_name'),
        ]);

        if (entriesRes.error) throw entriesRes.error;
        if (profilesRes.error) throw profilesRes.error;

        timeEntries = (entriesRes.data || []) as TimeEntry[];
        (profilesRes.data || []).forEach((p: any) => profilesMap.set(p.id, { first_name: p.first_name, last_name: p.last_name }));
      }

      // Employés actifs = ceux qui ont des entrées sur la période
      const uniqueUserIds = new Set(timeEntries.map(e => e.user_id));
      activeEmployeesCount = uniqueUserIds.size;

      const totalMinutes = timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
      const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

      // Heures par jour
      const numDays = Math.round((endOfWeek.getTime() - startOfWeek.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const dailyHours = [];
      const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

      for (let i = 0; i < numDays; i++) {
        const day = new Date(startOfWeek);
        day.setDate(day.getDate() + i);
        const dayStart = new Date(day);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(day);
        dayEnd.setHours(23, 59, 59, 999);

        const dayEntries = timeEntries.filter(entry => {
          const entryDate = new Date(entry.start_time);
          return entryDate >= dayStart && entryDate <= dayEnd;
        });

        const dayMinutes = dayEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
        dailyHours.push({
          day: dayNames[day.getDay()],
          hours: Math.round((dayMinutes / 60) * 10) / 10,
        });
      }

      // Performance par employé
      const employeeStats = new Map<string, number>();
      timeEntries.forEach(entry => {
        const profile = profilesMap.get(entry.user_id);
        if (profile) {
          const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'N/A';
          employeeStats.set(name, (employeeStats.get(name) || 0) + (entry.duration_minutes || 0));
        }
      });

      const employeePerformance = Array.from(employeeStats.entries())
        .map(([name, minutes]) => ({ name, hours: Math.round((minutes / 60) * 10) / 10 }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 10);

      // Distribution par projet
      const projectStats = new Map<string, number>();
      timeEntries.forEach(entry => {
        const projectName = entry.projects?.name ?? 'Sans projet';
        projectStats.set(projectName, (projectStats.get(projectName) || 0) + (entry.duration_minutes || 0));
      });

      const projectDistribution = Array.from(projectStats.entries())
        .map(([name, minutes]) => {
          const hours = Math.round((minutes / 60) * 10) / 10;
          const percentage = totalHours > 0 ? Math.round((hours / totalHours) * 100) : 0;
          return { name, hours, percentage };
        })
        .sort((a, b) => b.hours - a.hours);

      setReportData({
        weekStart: startOfWeek.toLocaleDateString('fr-FR'),
        weekEnd: endOfWeek.toLocaleDateString('fr-FR'),
        stats: {
          activeEmployees: activeEmployeesCount,
          totalHours,
          productivity: totalHours > 0 ? Math.min(100, Math.round((totalHours / (activeEmployeesCount * 40)) * 100)) : 0,
          avgHoursPerDay: numDays > 0 ? Math.round((totalHours / numDays) * 10) / 10 : 0,
        },
        dailyHours,
        employeePerformance,
        projectDistribution,
      });
    } catch (error) {
      console.error('Erreur lors du chargement du rapport hebdomadaire:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le rapport hebdomadaire",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [customStart?.getTime(), customEnd?.getTime()]);

  useEffect(() => {
    fetchWeeklyReportData();
  }, [fetchWeeklyReportData]);

  return { reportData, loading, refetch: fetchWeeklyReportData };
};
